import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

type PostHogClient = (typeof import('./posthog-client'))['default'];

let posthogClientPromise: Promise<PostHogClient | null> | null = null;

const getPostHogClient = (): Promise<PostHogClient | null> => {
  if (!POSTHOG_API_KEY) return Promise.resolve(null);

  if (!posthogClientPromise) {
    posthogClientPromise = import('./posthog-client')
      .then(({ default: posthog }) => {
        posthog.init(POSTHOG_API_KEY, {
          api_host: POSTHOG_HOST,
          person_profiles: 'identified_only',
          capture_pageview: false,
          capture_pageleave: true,
        });
        return posthog;
      })
      .catch((error) => {
        console.warn('PostHog failed to load:', error);
        posthogClientPromise = null;
        return null;
      });
  }

  return posthogClientPromise;
};

const withPostHog = (operation: (client: PostHogClient) => void) => {
  void getPostHogClient().then((client) => {
    if (client) operation(client);
  });
};

/**
 * PostHog provider component that automatically identifies users
 * and sets up user properties based on authentication state
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const { user, userDetails } = useAuth();

  useEffect(() => {
    if (!POSTHOG_API_KEY) return;

    let cancelled = false;

    void getPostHogClient().then((posthog) => {
      if (!posthog || cancelled) return;

      if (user && userDetails) {
        posthog.identify(user.id, {
          email: user.email,
          user_type: userDetails.user_type,
          country: userDetails.country,
          created_at: user.created_at,
        });
      } else {
        posthog.reset();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [user, userDetails]);

  return <>{children}</>;
}

/**
 * Utility functions for tracking events
 */
export const analytics = {
  /**
   * Track a custom event
   */
  track: (eventName: string, properties?: Record<string, any>) => {
    withPostHog((posthog) => posthog.capture(eventName, properties));
  },

  /**
   * Track a page view
   */
  page: (pageName?: string, properties?: Record<string, any>) => {
    const currentUrl = window.location.href;
    withPostHog((posthog) => {
      posthog.capture('$pageview', {
        $current_url: currentUrl,
        page_name: pageName,
        ...properties,
      });
    });
  },

  /**
   * Set user properties
   */
  setUserProperties: (properties: Record<string, any>) => {
    withPostHog((posthog) => posthog.setPersonProperties(properties));
  },

  /**
   * Track campaign events
   */
  campaign: {
    created: (campaignId: string, data: Record<string, any>) => {
      analytics.track('campaign_created', { campaign_id: campaignId, ...data });
    },
    published: (campaignId: string) => {
      analytics.track('campaign_published', { campaign_id: campaignId });
    },
    applied: (campaignId: string, applicationId: string) => {
      analytics.track('campaign_applied', { campaign_id: campaignId, application_id: applicationId });
    },
    applicationAccepted: (campaignId: string, applicationId: string) => {
      analytics.track('application_accepted', { campaign_id: campaignId, application_id: applicationId });
    },
    applicationRejected: (campaignId: string, applicationId: string) => {
      analytics.track('application_rejected', { campaign_id: campaignId, application_id: applicationId });
    },
    postSubmitted: (campaignId: string, postId: string) => {
      analytics.track('post_submitted', { campaign_id: campaignId, post_id: postId });
    },
    fundsAdded: (campaignId: string, amount: number) => {
      analytics.track('campaign_funds_added', { campaign_id: campaignId, amount });
    },
  },

  /**
   * Track payment events
   */
  payment: {
    initiated: (amount: number, method: string) => {
      analytics.track('payment_initiated', { amount, method });
    },
    completed: (amount: number, method: string, transactionId?: string) => {
      analytics.track('payment_completed', { amount, method, transaction_id: transactionId });
    },
    failed: (amount: number, method: string, error?: string) => {
      analytics.track('payment_failed', { amount, method, error });
    },
  },

  /**
   * Track auth events
   */
  auth: {
    signUp: (userType: string) => {
      analytics.track('user_signed_up', { user_type: userType });
    },
    signIn: (userType: string) => {
      analytics.track('user_signed_in', { user_type: userType });
    },
    signOut: () => {
      analytics.track('user_signed_out');
    },
  },
};
